import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TeamHeader } from '../header/TeamHeader';
import { Loading } from '../../../ui/loading/Loading';
import StratIcon from '../../../../assets/components_icons/StratIcon';
import { zynity_api } from '../../../../config/external-routes';
import { getOrganizationTeam } from '../../../../store/user_organizations/teams/team';
import { ContentEditorModal, ContentEditorValues } from './components/ContentEditorModal';
import { CategoryAttachments } from './components/CategoryAttachments';
import './StrategiesPage.css';

type StrategyCategory = {
    id: number;
    title: string;
    description: string | null;
    listOrder: number;
    idAuthor: number | null;
    dateCreated: string | null;
    dateModified: string | null;
};

type StrategyInitiativeSummary = {
    id: number;
    title: string;
    idCategory: number | null;
    percentComplete: number | null;
    startDate: string | null;
    endDate: string | null;
    listOrder: number;
    description?: string | null;
    dateCreated?: string | null;
    dateModified?: string | null;
    progress?: string | null;
};

type Tab = 'categories' | 'discussion';

const formatDate = (value: string | null | undefined) => {
    if (!value) {
        return '';
    }
    return new Date(value).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Matches Yii's `date_diff($dateCreated, NOW)->format("%R%a days")` — a signed
// relative day count ("+12 days" while open, would go negative only if dateCreated
// were somehow in the future).
const openDaysLabel = (dateCreated: string | null | undefined) => {
    if (!dateCreated) {
        return '';
    }
    const diffMs = Date.now() - new Date(dateCreated).getTime();
    const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
    const sign = days >= 0 ? '+' : '-';
    return `${sign}${Math.abs(days)} days`;
};

const initiativeStatusText = (initiative: StrategyInitiativeSummary) => {
    const percent = initiative.percentComplete ?? 0;
    if (percent === 0) {
        return 'Not started';
    }
    if (percent === 100) {
        return 'Completed';
    }
    const endDateFormatted = initiative.endDate
        ? new Date(initiative.endDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit' })
        : '';
    return `${percent}% · Due ${endDateFormatted}`;
};

// Matches Yii's status-dropdown timeline bar calculation exactly (index_v2.php's
// nested initiative row): elapsed % of the initiative's own start/end window, not
// percentComplete itself.
const timelineProgress = (initiative: StrategyInitiativeSummary) => {
    const start = initiative.startDate ? new Date(initiative.startDate).getTime() : null;
    const end = initiative.endDate ? new Date(initiative.endDate).getTime() : null;
    const now = Date.now();
    if (!start || !end) {
        return { percent: 0, label: 'Not started' };
    }
    if (now < start) {
        return { percent: 0, label: 'Not started' };
    }
    if (now > end) {
        return { percent: 100, label: 'Completed' };
    }
    const totalDays = Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)));
    const elapsedDays = Math.round((now - start) / (24 * 60 * 60 * 1000));
    return { percent: Math.min(100, (elapsedDays / totalDays) * 100), label: 'In progress' };
};

export const StrategiesHeadlinesPage = () => {
    const { organization_id, team_id } = useParams();
    const dispatch = useDispatch();
    const authenticatedUser = useSelector((state: any) => state.auth);
    const organizationTeam = useSelector((state: any) => state.organization_team);
    const enabled = !!authenticatedUser?.strategies_enabled;
    const teamMembers: any[] = useMemo(() => organizationTeam?.data?.members ?? [], [organizationTeam?.data?.members]);
    const memberId = (member: any) => (member?.People_id !== undefined && member?.People_id !== null ? member.People_id : member?.id);
    const isFacilitator = teamMembers.some(
        (m: any) => Number(memberId(m)) === Number(authenticatedUser?.id) && m.isFacilitator === 'Yes'
    );
    const canEditRow = (row: StrategyCategory) => isFacilitator || Number(row.idAuthor) === Number(authenticatedUser?.id);

    useEffect(() => {
        if (organization_id && team_id) {
            dispatch(getOrganizationTeam({ organization_id, team_id }) as any);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization_id, team_id]);

    const [activeTab, setActiveTab] = useState<Tab>('categories');
    const [categories, setCategories] = useState<StrategyCategory[]>([]);
    const [topics, setTopics] = useState<StrategyCategory[]>([]);
    const [initiatives, setInitiatives] = useState<StrategyInitiativeSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [editingCategory, setEditingCategory] = useState<StrategyCategory | null>(null);
    const [expandedInitiativeId, setExpandedInitiativeId] = useState<number | null>(null);
    const [openStatusDropdownId, setOpenStatusDropdownId] = useState<number | null>(null);

    const endpoint = (path = '') =>
        zynity_api.organization_team_strategies_categories
            .replace('{organization_id}', organization_id ?? '')
            .replace('{team_id}', team_id ?? '') + path;

    const fetchRows = async () => {
        if (!organization_id || !team_id) {
            return;
        }
        setLoading(true);
        try {
            const response = await axios.get(endpoint(), {
                params: activeTab === 'discussion' ? { type: 'Message' } : undefined,
            });
            if (activeTab === 'discussion') {
                setTopics(response.data.topics ?? []);
            } else {
                setCategories(response.data.categories ?? []);
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Load failed', text: 'Strategies could not be loaded for this team.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (enabled) {
            fetchRows();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization_id, team_id, activeTab, enabled]);

    // Nested per-category Initiatives list (Yii's `.initiative-list`, `_categories_v2.php`)
    // — only relevant to the Categories tab (Discussion topics never have a matching
    // idCategory), fetched once via the same team-wide endpoint the Initiatives page
    // uses and grouped client-side, rather than a new per-category backend call.
    useEffect(() => {
        const fetchInitiatives = async () => {
            if (!organization_id || !team_id || !enabled || activeTab !== 'categories') {
                return;
            }
            try {
                const response = await axios.get(
                    zynity_api.organization_team_strategies
                        .replace('{organization_id}', organization_id)
                        .replace('{team_id}', team_id)
                );
                setInitiatives(response.data.initiatives ?? []);
            } catch (error) {
                setInitiatives([]);
            }
        };
        fetchInitiatives();
    }, [organization_id, team_id, enabled, activeTab]);

    const initiativesForCategory = (categoryId: number) =>
        initiatives
            .filter((i) => Number(i.idCategory) === Number(categoryId))
            .sort((a, b) => (a.listOrder ?? 0) - (b.listOrder ?? 0));

    const handleCreate = async (values: ContentEditorValues) => {
        setSaving(true);
        try {
            const response = await axios.post(endpoint(), {
                title: values.title,
                description: values.description || null,
                type: activeTab === 'discussion' ? 'Message' : 'Category',
            });

            // Links/Uploads (Phase 6, §10) can only be attached once the row has an id,
            // so — matching Yii's real "type several, submit together" UX with a
            // create-then-attach sequence instead of one atomic multipart POST — fire
            // these off right after creation succeeds, before refreshing the list.
            const newId = response.data.category?.id ?? response.data.topic?.id;
            if (newId) {
                const linksUrl = zynity_api.organization_team_strategies_category_attachment_links
                    .replace('{organization_id}', organization_id ?? '')
                    .replace('{team_id}', team_id ?? '')
                    .replace('{category_id}', String(newId));
                const uploadsUrl = zynity_api.organization_team_strategies_category_attachment_uploads
                    .replace('{organization_id}', organization_id ?? '')
                    .replace('{team_id}', team_id ?? '')
                    .replace('{category_id}', String(newId));

                await Promise.all([
                    ...(values.links ?? []).map((url) => axios.post(linksUrl, { url })),
                    ...(values.uploadedFileNames ?? []).map((file_name) => axios.post(uploadsUrl, { file_name })),
                ]);
            }

            setShowModal(false);
            fetchRows();
        } catch (error: any) {
            const message = error?.response?.data?.error ?? 'This could not be saved.';
            Swal.fire({ icon: 'error', title: 'Save failed', text: typeof message === 'string' ? message : 'This could not be saved.' });
        } finally {
            setSaving(false);
        }
    };

    const handleEditSubmit = async (values: ContentEditorValues) => {
        if (!editingCategory) {
            return;
        }
        setSaving(true);
        try {
            const itemUrl = zynity_api.organization_team_strategies_category_item
                .replace('{organization_id}', organization_id ?? '')
                .replace('{team_id}', team_id ?? '')
                .replace('{category_id}', String(editingCategory.id));
            await axios.put(itemUrl, {
                title: values.title,
                description: values.description || null,
            });

            const linksUrl = zynity_api.organization_team_strategies_category_attachment_links
                .replace('{organization_id}', organization_id ?? '')
                .replace('{team_id}', team_id ?? '')
                .replace('{category_id}', String(editingCategory.id));
            const uploadsUrl = zynity_api.organization_team_strategies_category_attachment_uploads
                .replace('{organization_id}', organization_id ?? '')
                .replace('{team_id}', team_id ?? '')
                .replace('{category_id}', String(editingCategory.id));

            await Promise.all([
                ...(values.links ?? []).map((url) => axios.post(linksUrl, { url })),
                ...(values.uploadedFileNames ?? []).map((file_name) => axios.post(uploadsUrl, { file_name })),
            ]);

            setEditingCategory(null);
            fetchRows();
        } catch (error: any) {
            const message = error?.response?.data?.error ?? 'This could not be saved.';
            Swal.fire({ icon: 'error', title: 'Save failed', text: typeof message === 'string' ? message : 'This could not be saved.' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (category: StrategyCategory) => {
        const result = await Swal.fire({
            title: 'Delete this?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(
                zynity_api.organization_team_strategies_category_item
                    .replace('{organization_id}', organization_id ?? '')
                    .replace('{team_id}', team_id ?? '')
                    .replace('{category_id}', String(category.id))
            );
            fetchRows();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This could not be deleted.' });
        }
    };

    const reorderCategory = async (row: StrategyCategory, newOrder: number) => {
        const rows = activeTab === 'discussion' ? topics : categories;
        const withoutRow = rows.filter((r) => r.id !== row.id).sort((a, b) => a.listOrder - b.listOrder);
        const orderedIds = [
            ...withoutRow.slice(0, newOrder - 1).map((r) => r.id),
            row.id,
            ...withoutRow.slice(newOrder - 1).map((r) => r.id),
        ];
        try {
            await axios.put(
                zynity_api.organization_team_strategies_categories_reorder
                    .replace('{organization_id}', organization_id ?? '')
                    .replace('{team_id}', team_id ?? ''),
                { ordered_ids: orderedIds }
            );
            fetchRows();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
        }
    };

    const reorderInitiative = async (categoryId: number, initiative: StrategyInitiativeSummary, newOrder: number) => {
        const rows = initiativesForCategory(categoryId);
        const withoutRow = rows.filter((r) => r.id !== initiative.id);
        const orderedIds = [
            ...withoutRow.slice(0, newOrder - 1).map((r) => r.id),
            initiative.id,
            ...withoutRow.slice(newOrder - 1).map((r) => r.id),
        ];
        try {
            await axios.put(
                zynity_api.organization_team_strategies_reorder
                    .replace('{organization_id}', organization_id ?? '')
                    .replace('{team_id}', team_id ?? ''),
                { ordered_ids: orderedIds }
            );
            const response = await axios.get(
                zynity_api.organization_team_strategies
                    .replace('{organization_id}', organization_id ?? '')
                    .replace('{team_id}', team_id ?? '')
            );
            setInitiatives(response.data.initiatives ?? []);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
        }
    };

    if (!enabled) {
        return (
            <>
                <TeamHeader suffixRoute="/strategies" />
                <div className="container mt-5">
                    <p className="text-muted">This feature is not enabled for your account.</p>
                </div>
            </>
        );
    }

    const rows = activeTab === 'discussion' ? topics : categories;
    const visibleRows = filterText.trim() === ''
        ? rows
        : rows.filter((row) => row.title.toLowerCase().includes(filterText.trim().toLowerCase()));
    const sortedRows = [...visibleRows].sort((a, b) => a.listOrder - b.listOrder);

    const renderInitiativeRow = (initiative: StrategyInitiativeSummary, category: StrategyCategory) => {
        const categoryInitiatives = initiativesForCategory(category.id);
        const statusOpen = openStatusDropdownId === initiative.id;
        const timeline = timelineProgress(initiative);
        const initiativeExpanded = expandedInitiativeId === initiative.id;
        return (
            <div className="initiative-row" key={initiative.id}>
                <div className="initiative-row-left">
                    <div className="initiative-order">
                        <select
                            className="initiative-order-select"
                            value={initiative.listOrder}
                            onChange={(e) => reorderInitiative(category.id, initiative, Number(e.target.value))}
                        >
                            {categoryInitiatives.map((_, index) => (
                                <option key={index + 1} value={index + 1}>
                                    {index + 1}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div
                        className="initiative-title"
                        role="button"
                        onClick={() => setExpandedInitiativeId((current) => (current === initiative.id ? null : initiative.id))}
                    >
                        <span className="initiative-title-text">{initiative.title}</span>
                        <i className="fa fa-chevron-down initiative-title-icon" aria-hidden="true" />
                    </div>
                </div>
                <div className="initiative-row-right">
                    <div className="initiative-meta">
                        <span
                            className="initiative-status-pill"
                            title={`Completion: ${initiative.percentComplete ?? 0}%, Dates: ${initiative.startDate ?? ''} to ${initiative.endDate ?? ''}`}
                        >
                            <span className="status-percent">{initiativeStatusText(initiative)}</span>
                        </span>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                                type="button"
                                className="strategies-status-btn"
                                title="View Status Details"
                                onClick={() => setOpenStatusDropdownId((current) => (current === initiative.id ? null : initiative.id))}
                            >
                                <i className="fa fa-info-circle" aria-hidden="true" />
                            </button>
                            {statusOpen && (
                                <div className="strategies-status-dropdown show">
                                    <div className="strategies-status-dropdown-header">Initiative Status</div>
                                    <div className="strategies-status-dropdown-item">
                                        <div className="strategies-status-dropdown-label">Completion</div>
                                        <div className="strategies-status-dropdown-value">{initiative.percentComplete ?? 0}%</div>
                                    </div>
                                    <div className="strategies-status-dropdown-item">
                                        <div className="strategies-status-dropdown-label">Start Date</div>
                                        <div className="strategies-status-dropdown-value">{formatDate(initiative.startDate)}</div>
                                    </div>
                                    <div className="strategies-status-dropdown-item">
                                        <div className="strategies-status-dropdown-label">End Date</div>
                                        <div className="strategies-status-dropdown-value">{formatDate(initiative.endDate)}</div>
                                    </div>
                                    <div className="strategies-status-timeline">
                                        <div className="strategies-status-dropdown-label" style={{ marginBottom: '8px' }}>
                                            Timeline
                                        </div>
                                        <div className="strategies-status-timeline-container">
                                            <div className="strategies-status-timeline-label">
                                                Full bar = Initiative period ({formatDate(initiative.startDate)} to {formatDate(initiative.endDate)})
                                            </div>
                                            <div
                                                className="strategies-status-timeline-bar"
                                                title={`Timeline: ${timeline.label} (${Math.round(timeline.percent)}% elapsed)`}
                                            >
                                                <div
                                                    className="strategies-status-timeline-progress"
                                                    style={{ width: `${timeline.percent}%` }}
                                                    title={`Elapsed time: ${Math.round(timeline.percent)}%`}
                                                />
                                            </div>
                                            <div className="strategies-status-timeline-legend">
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <span style={{ display: 'inline-block', width: '12px', height: '8px', background: '#4CAF50', borderRadius: '2px' }} />
                                                    Elapsed
                                                </span>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    <span style={{ display: 'inline-block', width: '12px', height: '8px', background: '#e8e8e8', borderRadius: '2px' }} />
                                                    Remaining
                                                </span>
                                            </div>
                                            <div className="strategies-status-timeline-dates">
                                                <span>{formatDate(initiative.startDate)}</span>
                                                <span>{formatDate(initiative.endDate)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <Link
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/${initiative.id}`}
                            title="Go to Worksheet"
                            style={{
                                display: 'inline-block',
                                border: '1px solid #d0d0d0',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#666',
                                background: '#fff',
                                textDecoration: 'none',
                            }}
                        >
                            WS
                        </Link>
                    </div>
                </div>
                {initiativeExpanded && (
                    <div className="initiative-details-panel" style={{ background: '#fff', border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 6px 6px', padding: '12px 16px', marginTop: '4px' }}>
                        {initiative.description && (
                            <div className="small mb-2" dangerouslySetInnerHTML={{ __html: initiative.description }} />
                        )}
                        <div className="d-flex gap-3 small text-muted mb-2" style={{ background: '#F6F6F6', padding: '4px 8px' }}>
                            <span>Created: {formatDate(initiative.dateCreated)}</span>
                            <span>Last Edit: {formatDate(initiative.dateModified ?? initiative.dateCreated)}</span>
                            <span>Open: {openDaysLabel(initiative.dateCreated)}</span>
                        </div>
                        {initiative.progress && (
                            <div className="small">
                                <strong>Progress:</strong>
                                <div dangerouslySetInnerHTML={{ __html: initiative.progress }} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderRow = (row: StrategyCategory) => {
        const canEdit = canEditRow(row);
        const categoryInitiatives = activeTab === 'categories' ? initiativesForCategory(row.id) : [];
        const expanded = expandedId === row.id;
        return (
            <div className="topic-card" key={row.id}>
                <div className="topic-header" style={{ backgroundColor: '#FFFADE' }}>
                    <div className="topic-header-left">
                        <div className="topic-order">
                            <select
                                className="topic-order-select"
                                value={row.listOrder}
                                onChange={(e) => reorderCategory(row, Number(e.target.value))}
                            >
                                {sortedRows.map((_, index) => (
                                    <option key={index + 1} value={index + 1}>
                                        {index + 1}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div
                            className="topic-title"
                            role="button"
                            onClick={() => setExpandedId((current) => (current === row.id ? null : row.id))}
                        >
                            <span className="topic-title-text">{row.title}</span>
                            <i className="fa fa-chevron-down topic-title-icon" aria-hidden="true" />
                        </div>
                    </div>
                    <div className="topic-header-right">
                        <div className="topic-actions">
                            {canEdit && (
                                <i
                                    role="button"
                                    title="Edit"
                                    className="fa fa-pencil-square-o"
                                    onClick={() => setEditingCategory(row)}
                                />
                            )}
                            <a
                                href="#"
                                title="New Comment"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setExpandedId(row.id);
                                }}
                            >
                                <i className="far fa-comment-dots" aria-hidden="true" />
                            </a>
                        </div>
                        <Dropdown className="topic-menu">
                            <Dropdown.Toggle
                                as="span"
                                role="button"
                                aria-label="Category actions"
                                className="fa fa-bars"
                                style={{ cursor: 'pointer' }}
                            />
                            <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => setExpandedId(row.id)}>Comment</Dropdown.Item>
                                {canEdit && <Dropdown.Item onClick={() => setEditingCategory(row)}>Edit</Dropdown.Item>}
                                {canEdit && isFacilitator && (
                                    <Dropdown.Item onClick={() => handleDelete(row)}>Remove</Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                {expanded && (
                    <div style={{ border: 'thin solid lightgray', borderTop: 'none', padding: '8px 12px', boxSizing: 'border-box' }}>
                        {row.description && (
                            <div className="small mb-2" dangerouslySetInnerHTML={{ __html: row.description }} />
                        )}
                        <div className="d-flex gap-3 small text-muted mb-2" style={{ background: '#F6F6F6', padding: '4px 8px' }}>
                            {activeTab === 'discussion' ? (
                                <>
                                    <span className="strategies-comment-avatar" style={{ width: '20px', height: '20px', fontSize: '10px' }}>
                                        {(() => {
                                            const author = teamMembers.find((m: any) => Number(memberId(m)) === Number(row.idAuthor));
                                            if (!author) {
                                                return '--';
                                            }
                                            return author.FirstName
                                                ? `${author.FirstName.substring(0, 1)}${(author.LastName ?? '').substring(0, 1)}`.toUpperCase()
                                                : (author.EmailAddress ?? '').substring(0, 2).toUpperCase();
                                        })()}
                                    </span>
                                    <span>{formatDate(row.dateCreated)}</span>
                                </>
                            ) : (
                                <>
                                    <span>Created: {formatDate(row.dateCreated)}</span>
                                    <span>Last Edit: {formatDate(row.dateModified ?? row.dateCreated)}</span>
                                    <span>Open: {openDaysLabel(row.dateCreated)}</span>
                                </>
                            )}
                        </div>
                        {organization_id && team_id && (
                            <CategoryAttachments
                                organizationId={organization_id}
                                teamId={team_id}
                                categoryId={row.id}
                                allowEdit={canEdit}
                                currentUserId={authenticatedUser?.id}
                            />
                        )}
                        {activeTab === 'categories' && categoryInitiatives.length > 0 && (
                            <div className="initiative-list">
                                {categoryInitiatives.map((initiative) => renderInitiativeRow(initiative, row))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <TeamHeader suffixRoute="/strategies" />
            <div className="container mb-5 strategies-app">
                <div className="strategies-app-header d-flex flex-wrap justify-content-between align-items-center mt-4">
                    <div className="d-flex align-items-center gap-2">
                        <StratIcon style={{ width: '30px', height: '30px' }} />
                        <div>
                            <h2 className="mb-0">Strategies</h2>
                            <div className="text-muted small">(Our Future)</div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Link
                            className="btn btn-teal"
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/new`}
                        >
                            New Worksheet +
                        </Link>
                        <Dropdown>
                            <Dropdown.Toggle className="strategies-ellipsis-btn" aria-label="More actions">
                                •••
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                                <Dropdown.Item
                                    as={Link}
                                    to={`/organizations/${organization_id}/teams/${team_id}/document-library`}
                                >
                                    Docs
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                {/* Confirmed live 2026-08-27+ (`_strategies_app_header.php`'s `swh-center`):
                both Categories and Initiatives are always real, clickable links, on their
                own row below the title/CTA row — the current page gets `is-active`, it's
                never omitted or turned into a disabled label. */}
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Link
                        className="strategies-subnav-link is-active"
                        to={`/organizations/${organization_id}/teams/${team_id}/strategies`}
                    >
                        Categories
                    </Link>
                    <Link
                        className="strategies-subnav-link"
                        to={`/organizations/${organization_id}/teams/${team_id}/strategies/initiatives`}
                    >
                        Initiatives
                    </Link>
                </div>

                <div className="row mt-3 align-items-center">
                    <div className="col-auto">
                        {/* Confirmed live 2026-08-27 (index_v2.php): there's no separate global
                        "+ Add" control — each tab carries its own small "+" icon button
                        (showCreateCategoryModal()/showCreateMessageModal()) that opens the
                        matching create modal without switching the displayed tab. React
                        deliberately switches the tab too when its "+" is clicked, since the
                        modal's fields are driven by `activeTab` — a small, documented UX
                        simplification rather than threading a separate create-mode state. */}
                        <div className="btn-group" role="tablist">
                            <Button
                                as="span"
                                variant={activeTab === 'categories' ? 'warning' : 'outline-secondary'}
                                onClick={() => setActiveTab('categories')}
                                className="d-flex align-items-center gap-2"
                            >
                                Categories
                                <button
                                    type="button"
                                    aria-label="create new Category"
                                    title="create new Category"
                                    className="strategies-tab-add-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTab('categories');
                                        setShowModal(true);
                                    }}
                                >
                                    +
                                </button>
                            </Button>
                            <Button
                                as="span"
                                variant={activeTab === 'discussion' ? 'warning' : 'outline-secondary'}
                                onClick={() => setActiveTab('discussion')}
                                className="d-flex align-items-center gap-2"
                            >
                                Discussion
                                <button
                                    type="button"
                                    aria-label="create new Topic"
                                    title="create new Topic"
                                    className="strategies-tab-add-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTab('discussion');
                                        setShowModal(true);
                                    }}
                                >
                                    +
                                </button>
                            </Button>
                        </div>
                    </div>
                    <div className="col d-flex justify-content-end align-items-center gap-2">
                        <Form.Group className="d-flex align-items-center gap-2 mb-0" controlId="strategies-headlines-search">
                            <Form.Label className="mb-0 fw-bold">Search</Form.Label>
                            <Form.Control
                                style={{ maxWidth: '320px' }}
                                placeholder="Title, intent, category, authors, discussion..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                        </Form.Group>
                        <Button variant="outline-secondary" onClick={() => setFilterText('')}>
                            Clear
                        </Button>
                    </div>
                </div>

                <div className="mt-4">
                    {loading ? (
                        <Loading />
                    ) : sortedRows.length === 0 ? (
                        <p className="text-muted">{activeTab === 'discussion' ? 'No topics found in database.' : 'No Categories.'}</p>
                    ) : (
                        sortedRows.map(renderRow)
                    )}
                </div>
            </div>

            {/*
                Category and Topic use different field labels/placeholders live (confirmed
                2026-08-26 by reading index_v2.php's showCreateCategoryModal()/
                showCreateMessageModal() directly, after live testing surfaced the
                mismatch) — Category: "Context"/"Supporting Links"/"Supporting Files" with
                a descriptive title placeholder; Topic: generic "Story"/"Links"/"Uploads"
                with a plain "Topic *" placeholder. Also: live's modal title is "New Topic",
                not "New Message" — corrected here too.

                addLinkLabel="Add More Links" below: confirmed live 2026-08-27 — this page's
                own modal (index_v2.php) keeps the raw "Add More Links" text; unlike the
                Details-item/comment shared modal on the worksheet page, whose "Add More
                Links" gets runtime-normalized down to "Add Link" by worksheet_v1.js's
                normalizeModalAddButtonLabels(), which only targets #categoryContentModal/
                #milestoneFormModal, not this page's separate modal. ContentEditorModal's
                own default stays "Add Link" to match the more common (Details-item) callers.
            */}
            <ContentEditorModal
                show={showModal}
                title={activeTab === 'discussion' ? 'New Topic' : 'New Category'}
                titleLabel={activeTab === 'discussion' ? 'Topic' : 'Category'}
                titlePlaceholder={
                    activeTab === 'discussion'
                        ? 'Topic *'
                        : 'Enter category (e.g. Revenue, Growth, Customer Success)'
                }
                storyLabel={activeTab === 'discussion' ? 'Story' : 'Context'}
                storyPlaceholder={
                    activeTab === 'discussion' ? undefined : 'Describe the purpose or context of this category...'
                }
                linksLabel={activeTab === 'discussion' ? 'Links' : 'Supporting Links'}
                linksPlaceholder={
                    activeTab === 'discussion' ? 'https://...' : 'Add a related link (article, research, document)'
                }
                addLinkLabel="Add More Links"
                uploadsLabel={activeTab === 'discussion' ? 'Uploads' : 'Supporting Files'}
                saving={saving}
                showAttachments
                onHide={() => setShowModal(false)}
                onSubmit={handleCreate}
            />

            {/* Edit Category/Topic — matches Yii's showEditCategoryModal(), same
                _formNewEditCategory.php partial reused for both create and edit. */}
            <ContentEditorModal
                show={editingCategory !== null}
                title={activeTab === 'discussion' ? 'Edit Topic' : 'Edit Category'}
                titleLabel={activeTab === 'discussion' ? 'Topic' : 'Category'}
                storyLabel={activeTab === 'discussion' ? 'Story' : 'Context'}
                linksLabel={activeTab === 'discussion' ? 'Links' : 'Supporting Links'}
                addLinkLabel="Add More Links"
                uploadsLabel={activeTab === 'discussion' ? 'Uploads' : 'Supporting Files'}
                initialValues={{
                    title: editingCategory?.title ?? '',
                    description: editingCategory?.description ?? '',
                }}
                saving={saving}
                showAttachments
                onHide={() => setEditingCategory(null)}
                onSubmit={handleEditSubmit}
            />
        </>
    );
};
